import React from 'react';
import { Link } from 'react-router-dom';
import { 
  BedDouble, Bus, Utensils, Camera, Mountain, MapPin, 
  Route, Flame, Heart, Ticket, Snowflake, Headphones,
  ShieldCheck, Star, Users, Clock, Zap, CheckCircle2, ChevronRight,
  ArrowRight
} from 'lucide-react';
import { DESTINATION_SPECIFIC_DATA } from '../../constants/destinationData';
import { HOLIDAY_TYPES, HOW_IT_WORKS, BLOG_POSTS } from '../../mock';

const ICON_MAP = {
  BedDouble, Bus, Utensils, Camera, Mountain, MapPin, 
  Route, Flame, Heart, Ticket, Snowflake, Headphones,
  ShieldCheck, Star, Users, Clock, Zap, CheckCircle2
};

export function HolidaysStrip() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">Plan Your Escape</div>
          <h2 className="font-poppins text-[34px] lg:text-[44px] text-brand-blue leading-[1.1]">Pick Your <span className="text-brand-orange">Holiday Style</span></h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6">
          {HOLIDAY_TYPES.map((type, i) => (
            <Link key={i} to={type.slug === 'international' ? '/international' : type.slug === 'domestic' ? '/domestic' : type.slug === 'self-drive' ? '/self-drive' : `/holidays/${type.slug}`} className="group relative rounded-2xl overflow-hidden h-48 block">
               <img src={type.image} alt={type.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
               <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/80 to-transparent" />
               <div className="absolute inset-x-0 bottom-0 p-4">
                 <h3 className="text-white font-poppins font-bold text-[15px] lg:text-[17px]">{type.name}</h3>
                 <p className="text-white/70 text-[11px] lg:text-[12px] mt-1">{type.tagline}</p>
               </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function HowItWorksPreview() {
  return (
    <section className="py-20 bg-brand-grey">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">The Process</div>
          <h2 className="font-poppins text-[34px] lg:text-[44px] text-brand-blue leading-[1.1]">How It <span className="text-brand-orange">Works</span></h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {HOW_IT_WORKS.map((step, i) => (
            <div key={i} className="text-center relative">
              <div className="text-[40px] mb-4">{step.emoji}</div>
              <h3 className="font-poppins font-bold text-brand-blue text-[18px] mb-2">{step.title}</h3>
              <p className="text-brand-blue/60 text-[14px] leading-relaxed">{step.desc}</p>
              {i < HOW_IT_WORKS.length - 1 && (
                <div className="hidden lg:block absolute top-10 -right-4 text-brand-greyDark">
                  <ArrowRight size={24} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function BlogPreview() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
          <div>
            <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">Travel Stories</div>
            <h2 className="font-poppins text-[34px] lg:text-[44px] text-brand-blue leading-[1.1]">Our Latest <span className="text-brand-orange">Blog Posts</span></h2>
          </div>
          <Link to="/blog" className="text-brand-orange font-bold flex items-center gap-2 hover:gap-3 transition-all">
            View All Stories <ArrowRight size={18} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {BLOG_POSTS.slice(0, 3).map((post, i) => (
            <Link key={i} to={`/blog/${post.slug}`} className="group block">
              <div className="relative rounded-2xl overflow-hidden aspect-[16/10] mb-5">
                <img src={post.image} alt={post.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-[11px] font-bold text-brand-blue uppercase tracking-wider">
                  {post.category}
                </div>
              </div>
              <h3 className="font-poppins font-bold text-brand-blue text-[20px] leading-tight group-hover:text-brand-orange transition-colors mb-3">{post.title}</h3>
              <p className="text-brand-blue/60 text-[14px] line-clamp-2 leading-relaxed">{post.excerpt}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function AllInclusiveGrid({ slug = 'shimla' }) {
  const data = DESTINATION_SPECIFIC_DATA[slug] || DESTINATION_SPECIFIC_DATA['shimla'];
  const items = data.allInclusive;
  const destName = slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-block bg-brand-orange/10 text-brand-orange text-[11px] font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-4">
            Stress-Free Travel
          </div>
          <h2 className="font-poppins text-[32px] lg:text-[44px] text-brand-blue leading-tight mb-4">Everything Is <span className="text-brand-orange">Handled For You</span></h2>
          <p className="text-brand-blue/60 text-[16px] lg:text-[18px]">From your doorstep to the heart of {destName} - we take care of every detail.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
          {items.map((item, i) => {
            const Icon = ICON_MAP[item.icon] || CheckCircle2;
            return (
              <div key={i} className="flex items-center gap-4 p-5 rounded-2xl bg-brand-grey/50 border border-brand-greyDark hover:border-brand-orange/30 transition-colors group">
                <div className="w-10 h-10 rounded-xl bg-white text-brand-orange flex items-center justify-center shadow-sm group-hover:bg-brand-orange group-hover:text-white transition-all">
                  <Icon size={20} />
                </div>
                <span className="text-brand-blue font-semibold text-[14px] lg:text-[15px] leading-tight">{item.text}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function ExperienceGallery({ slug = 'shimla' }) {
  const data = DESTINATION_SPECIFIC_DATA[slug] || DESTINATION_SPECIFIC_DATA['shimla'];
  const gallery = data.experienceGallery;
  const destName = slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  return (
    <section className="py-20 bg-brand-blue text-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12 gap-6">
          <div className="max-w-2xl">
            <div className="text-brand-orange text-[13px] font-bold uppercase tracking-widest mb-3">Iconic Landmarks</div>
            <h2 className="font-poppins text-[32px] lg:text-[44px] leading-tight">The {destName} You <span className="text-brand-orange">Came To See</span></h2>
            <p className="text-white/60 mt-4 text-[16px]">Every package covers the most sacred and breathtaking destinations of your journey.</p>
          </div>
          <button 
            onClick={() => window.dispatchEvent(new CustomEvent('mct:openEnquiry'))}
            className="flex items-center gap-2 text-brand-orange font-bold hover:gap-3 transition-all"
          >
            Get Custom Itinerary <ChevronRight size={18}/>
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {gallery.map((item, i) => (
            <div key={i} className="group relative rounded-3xl overflow-hidden aspect-[4/5]">
              <img 
                src={item.image} 
                alt={item.title} 
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-blue via-brand-blue/20 to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />
              <div className="absolute inset-x-0 bottom-0 p-6 translate-y-2 group-hover:translate-y-0 transition-transform">
                <h3 className="font-poppins font-bold text-[20px] mb-1">{item.title}</h3>
                <p className="text-white/70 text-[13px] line-clamp-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function TrustBanner() {
  const features = [
    { icon: ShieldCheck, title: 'Govt. Registered', desc: 'Himachal & J&K Tourism approved' },
    { icon: Clock, title: '15+ Years Exp', desc: 'Crafting Himalayan memories since 2009' },
    { icon: Users, title: '10 Lakh+ Travelers', desc: 'Indias most loved travel family' },
    { icon: Star, title: '4.9/5 Rated', desc: 'Highest rated DMC in North India' },
    { icon: Zap, title: 'Instant Quote', desc: 'Get your customized plan in 2 mins' },
    { icon: Headphones, title: '24/7 Support', desc: 'Always reachable during your trip' },
  ];

  return (
    <section className="py-12 bg-white border-y border-brand-greyDark">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-8">
          {features.map((f, i) => (
            <div key={i} className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-brand-orange/10 text-brand-orange mb-3">
                <f.icon size={24} />
              </div>
              <div className="font-poppins font-bold text-brand-blue text-[15px] leading-tight mb-1">{f.title}</div>
              <div className="text-brand-blue/50 text-[11px] font-medium leading-tight">{f.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
